/* eslint-disable no-nested-ternary */
import React, { useEffect, useState, useCallback } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { Row, Col } from 'react-bootstrap';
import {
  Button,
  Typography,
  Card,
  Table,
  CardContent,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableFooter,
  TextField,
} from '@material-ui/core';
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import dayjs from 'dayjs';
import moment from 'moment';
import Select from 'react-select';
import { useIntl } from 'react-intl';
import FilterCategoriesTree from '../users/users/components/FilterNavigate';
import { SHeadTableCell } from '../../../components/styledComponents/Tables';
import Preloader from '../../../components/other/Preloader/Preloader';
import { setLayoutSubheader, setLayoutFooter } from '../../../utils/layout';
import { IAppState } from '../../../store/rootDuck';
import { actions as transactionActions } from '../../../store/ducks/transactions.duck';
import homeStyles from '../../../constants/homeStyles';
import { useStyles } from './hooks/useStyles';
import { useFormatMessage } from '../../../hooks';
import { useSearchUsers } from '../users/users/hooks/useSearchUsers';
import { IContentTransaction, IITransactionParams } from '../../../interfaces/transaction';
import TablePaginator from '../../../components/tableComponents/TablePaginator/TablePaginator';
import AlertTransactionDetail from './components/AlertTransactionDetail';
import DownloadIcon from './components/DownloadIcon';


export const typeList: string[] = [
  'DEPOSIT',
  'WITHDRAWAL',
  'SWAP',
  'BUY',
  'SELL',
  'SEND',
  'RECEIVE',
];

export const currencyList: string[] = [
  'BTC',
  'ETH',
  'SOL',
  'TRX',
];

export const statusList: string[] = [
  'CANCELLED',
  'COMPLETED',
  'EXPIRED',
  'ERROR',
  'PENDING',
  'OPEN',
  'CLOSED',
];

const TransactionPage: React.FC<TPropsFromRedux> = ({
  fetch,
  clear,
  transactions,
  loading,
  page,
  size,
  total,
}) => {
  const intl = useIntl();
  const classes = useStyles();
  const homeClasses = homeStyles();
  const fm = useFormatMessage();
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [selectedItem, setSelectedItem] = useState<IContentTransaction | null>(null);
  const [selectedTypeItems, setSelectedTypeItems] = useState<string>('');
  const [selectedSearchItems, setSelectedSearchItems] = useState<string>('');
  const [selectedCurrencyItems, setSelectedCurrencyItems] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const defaultType = { value: '', label: 'ALL TYPES' };
  const defaultCurrency = { value: '', label: 'ALL CURRENCY' };
  const defaultStatus = { value: '', label: 'ALL STATUS' };
  const [startDate, setStartDate] = useState<Date | null>(new Date('2024-01-01'));
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [filterParams, setFilterParams] = useState<IITransactionParams>({});
  const [isOpenTransactionDialog, setOpenTransactionDialog] = useState(false);

  const {
    fetchUsers,
    page: searchPage,
    perPage: searchPerPage,
  } = useSearchUsers();

  const selectStyles = {
  container: (provided: any) => ({
    ...provided,
    marginBottom: 16,
    width: '32.5%',
    marginRight: 8,
  }),
  control: (provided: any, state: any) => ({
    ...provided,
    border: 'none',
    backgroundColor: '#fff',
    boxShadow: '0.5px 2.5px 6.5px 0.5px rgba(0, 0, 0, .1)',
    borderRadius: '12px',
    minHeight: 52,
    height: 52,
  }),
  option: (provided: any, state: any) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#293B85' : '#fff',
    color: state.isSelected ? '#fff' : '#000',
    ':hover': {
      backgroundColor: '#293B85',
      color: '#fff',
    },
    height: 52,
  }),
  singleValue: (provided: any) => ({
    ...provided,
    color: '#293B85',
    fontWeight: 400,
    fontSize: 16
  }),
  placeholder: (provided: any) => ({
    ...provided,
    color: '#293B85',
    fontWeight: 400,
    fontSize: 16
  }),
};

  useEffect(() => {
    fetch({page, size});
    return () => {
      clear();
    };
  }, []);

  const hendleItem = useCallback(async(item: IContentTransaction) => {
    await setSelectedItem(item)
    setOpenTransactionDialog(true)
  }, []);

  const handleFilter = useCallback(() => {
    const params: { [key: string]: any } = {};
  
    if (selectedTypeItems) params.type = selectedTypeItems;
    if (selectedStatus) params.status = selectedStatus;
    if (selectedCurrencyItems) params.asset = selectedCurrencyItems;
    if (selectedSearchItems) params.searchText = selectedSearchItems;
  
    if (startDate) params.from = moment(startDate).startOf('day').toISOString();
    if (endDate) params.to = moment(endDate).endOf('day').toISOString();
  
    setFilterParams(params)
    fetch(params);
  }, [selectedTypeItems, selectedStatus, selectedCurrencyItems, startDate, endDate, selectedSearchItems]);
  
  const hendleClear = useCallback(() => {
    fetch({page, size})
    setSelectedStatus('')
    setSelectedTypeItems('')
    setSelectedCurrencyItems('')
  },
  [ searchPage, searchPerPage]
);

  const downloadCSV = () => {
  if (!transactions?.content?.length) return;

  const headers = ['Number', 'Username', 'Date', 'Type', 'Source value', 'Target value', 'Status'];
  const rows = transactions.content.map(item => [
    item.transactionId,
    item.fullName || '-',
    moment(item.opened).format('DD.MM.YYYY'),
    item.swap ? 'SWAP' : item.direction,
    `${item.cost || 0} ${item.quoteAsset}`,
    `${item.volumeExecuted || 0} ${item.baseAsset}`,
    item.status
  ]);

  const csvContent =
      'data:text/csv;charset=utf-8,' +
      [headers, ...rows]
        .map(e =>
          e
            .map(field => `"${String(field).replace(/"/g, '""')}"`)
            .join(',')
        )
        .join('\n');

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', 'transactions_history.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };


  setLayoutSubheader({
    title: intl.formatMessage({ id: 'SUBMENU.USER_BY_ROLE.LIST' }),
    breadcrumb: [],
  });
  setLayoutFooter({ show: true });

  if (loading) return <Preloader />;

  return (
    <div style={{ maxWidth: 1440, width: '100%', margin: '0 auto' }}>

      <Row>
        <Col>
          <div className={classes.container}>
            <div className={classes.filterColTop}>
              <FilterCategoriesTree
                setSelectedCategoryId={setSelectedCategoryId}
                selectedCategoryId={selectedCategoryId}
              />
            </div>

            <div style={{ flex: 1 }}>
              <div style={{display: 'flex', flexDirection: 'row', justifyContent:'space-between', marginBottom: '20px', position: 'relative'}}>
              <div style={{width: '40%'}}>
              <TextField
                variant='standard'
                type="search"
                placeholder='Search'
                style={{ padding: 0, margin: 0, height: 52, }}
                margin="normal"
                className="kt-width-full"
                name="search"
                onChange={(e) => setSelectedSearchItems(e.target.value)}
                InputProps={{
                  style: { 
                    backgroundColor: "#FFFFFF", 
                    padding: '0 20px',
                    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
                  },
                }}
                InputLabelProps={{
                  shrink: true,
                }}
              />

                <MuiPickersUtilsProvider
                  utils={DateFnsUtils}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      width: '100%'
                    }}>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        marginTop: 16,
                        boxShadow: '0.5px 2.5px 6.5px 0.5px rgba(0, 0, 0, .1)',
                        height: 52,
                        alignItems: 'center',
                        width: '48%',
                        justifyContent: 'center',
                        borderRadius: 12,
                        paddingLeft: 10
                      }}>
                      <KeyboardDatePicker
                        format="yyyy.MM.dd"
                        value={startDate}
                        onChange={date => {
                          if (date) setStartDate(date);
                        }}
                      />
                    </div>

                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        marginTop: 16,
                        boxShadow: '0.5px 2.5px 6.5px 0.5px rgba(0, 0, 0, .1)',
                        height: 52,
                        alignItems: 'center',
                        width: '48%',
                        justifyContent: 'center',
                        borderRadius: 12,
                        paddingLeft: 10
                      }}>
                      <KeyboardDatePicker
                        format="yyyy.MM.dd"
                        value={endDate}
                        onChange={date => {
                          if (date) setEndDate(date);
                        }}
                      />
                    </div>
                  </div>
                </MuiPickersUtilsProvider>
              </div>

              <div style={{width: '59%'}}>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}>
                  <Select
                    placeholder='ALL TYPES'
                    components={{ IndicatorSeparator: () => null }}
                    value={
                      selectedTypeItems
                        ? { label: selectedTypeItems, value: selectedTypeItems }
                        : undefined
                    }
                    styles={selectStyles}
                    onChange={item => {
                      if (item) {
                        setSelectedTypeItems(item.value);
                      }
                    }}
                    options={
                      typeList
                        ? [
                            defaultType,
                            ...typeList.map(item => ({
                              label: item,
                              value: item,
                            })),
                          ]
                        : [defaultType]
                    }
                  />

                  <Select
                    placeholder='ALL CURRENCY'
                    components={{ IndicatorSeparator: () => null }}
                    value={
                      selectedCurrencyItems
                        ? { label: selectedCurrencyItems, value: selectedCurrencyItems }
                        : undefined
                    }
                    styles={selectStyles}
                    onChange={item => {
                      if (item) {
                        setSelectedCurrencyItems(item.value);
                      }
                    }}
                    options={
                      currencyList
                        ? [
                          defaultCurrency,
                            ...currencyList.map(item => ({
                              label: item,
                              value: item,
                            })),
                          ]
                        : [defaultCurrency]
                    }
                  />

                  <Select
                    placeholder='ALL STATUS'
                    components={{ IndicatorSeparator: () => null }}
                    value={
                      selectedStatus
                        ? { label: selectedStatus, value: selectedStatus }
                        : undefined
                    }
                    styles={selectStyles}
                    onChange={item => {
                      if (item) {
                        setSelectedStatus(item.value);
                      }
                    }}
                    options={
                      statusList
                        ? [
                          defaultStatus,
                            ...statusList.map(item => ({
                              label: item,
                              value: item,
                            })),
                          ]
                        : [defaultStatus]
                    }
                  />
                </div>

                <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center'}}>
                  <Button
                    variant="contained"
                    color="primary"
                    style={{
                      height: 52,
                      borderRadius: 12,
                      width: '160px',
                      marginRight: 12,
                      fontSize: 16,
                    }}
                    onClick={handleFilter}
                  >
                    Apply
                  </Button>

                  <Button
                    variant="contained"
                    color='secondary'
                    style={{
                      height: 52,
                      borderRadius: 12,
                      width: '160px',
                      color: '#304497',
                      fontSize: 16,
                      boxShadow: 'none',
                    }}
                    onClick={hendleClear}
                  >
                    Reset
                  </Button>
                </div>
              </div>
              <Button
                variant='contained'
                color='secondary'
                style={{
                  height: 52,
                  borderRadius: 12,
                  color: '#304497',
                  fontSize: 16,
                  boxShadow: 'none',
                  position: 'absolute',
                  bottom: 0,
                  right: 5
                }}
                onClick={downloadCSV}
              >
                <DownloadIcon /> &nbsp; Download history
              </Button>
              </div>

              {loading ? (
                <div className={classes.preloaderContainer}>
                  <Preloader />
                </div>
              ) : !transactions?.content ? (
                <Typography className={classes.empty} component='h5' variant='h5'>
                  {fm('COMMON.LIST.EMPTY')}
                </Typography>
              ) : (
                <Card className={classes.tableContainer}>
                  <CardContent className={homeClasses.tableContainer}>
                    <Table aria-label='simple table'>
                      <TableHead>
                        <TableRow>
                          <SHeadTableCell>Number</SHeadTableCell>
                          <SHeadTableCell>Username</SHeadTableCell>
                          <SHeadTableCell>Date</SHeadTableCell>
                          <SHeadTableCell>Type</SHeadTableCell>
                          <SHeadTableCell>Source value</SHeadTableCell>
                          <SHeadTableCell>Target value</SHeadTableCell>
                          <SHeadTableCell>Status</SHeadTableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {transactions?.content.map(item => (
                          <TableRow
                            style={{
                              backgroundColor: selectedItem?.transactionId === item.transactionId ? '#F5F6F9' : '',
                              borderRadius: 12,
                              cursor: 'pointer'
                            }}
                            key={item.transactionId}>
                            <TableCell
                              onClick={() => hendleItem(item)}
                              style={{
                                border: 'none',
                                color: '#304497',
                                fontWeight: 400,
                                fontSize: 16,
                                borderTopLeftRadius: selectedItem?.transactionId === item.transactionId ? '12px' : '',
                                borderBottomLeftRadius: selectedItem?.transactionId === item.transactionId ? '12px' : '',
                              }}
                            >
                              {item.transactionId}
                            </TableCell>

                            <TableCell
                              onClick={() => hendleItem(item)}
                              style={{
                                border: 'none',
                                color: '#304497',
                                fontWeight: 400,
                                fontSize: 16,
                              }}
                            >
                              {item.fullName || '-'}
                            </TableCell>

                            <TableCell
                              onClick={() => hendleItem(item)}
                              style={{
                                border: 'none',
                                color: '#304497',
                                fontWeight: 400,
                                fontSize: 16,
                              }}
                            >
                              {moment(item.opened).format('DD.MM.YYYY')}
                            </TableCell>

                            <TableCell
                              onClick={() => hendleItem(item)}
                              style={{
                                border: 'none',
                                color: '#304497',
                                fontWeight: 400,
                                fontSize: 16,
                              }}
                            >
                              {item.swap ? 'SWAP' : item.direction}
                            </TableCell>

                            <TableCell
                              onClick={() => hendleItem(item)}
                              style={{
                                border: 'none',
                                color: '#304497',
                                fontWeight: 400,
                                fontSize: 16,
                              }}
                            >
                              {(item.cost || 0) + ' ' + item.quoteAsset}
                            </TableCell>

                            <TableCell
                              onClick={() => hendleItem(item)}
                              style={{
                                border: 'none',
                                color: '#304497',
                                fontWeight: 400,
                                fontSize: 16,
                              }}
                            >
                              {(item.volumeExecuted || 0) + ' ' + item.baseAsset}
                            </TableCell>

                            <TableCell
                              onClick={() => hendleItem(item)}
                              style={{
                                border: 'none',
                                fontWeight: 400,
                                fontSize: 16,
                                color: item.status === 'CANCELLED' ? '#DC1F1F' : '#1DA316',
                              }}
                            >
                              {item.status}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>

                      <TableFooter>
                        <TableRow style={{ border: 'none' }}>
                          <TablePaginator
                            page={page || 0}
                            realPerPage={20}
                            perPage={size}
                            total={total}
                            fetchRows={(data: any) => fetch({ ...data, ...filterParams })}
                            label=''
                          />
                        </TableRow>
                      </TableFooter>
                    </Table>
                  </CardContent>
                </Card>
              )}
              <AlertTransactionDetail
                    open={isOpenTransactionDialog}
                    transaction={selectedItem}
                    message={intl.formatMessage({ id: 'FILE.IMPORT.ALERT.TEXT' })}
                    handleClose={() => {
                        setOpenTransactionDialog(false)
                    }
                    }
                />
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};

const connector = connect(
  (state: IAppState) => ({
    loading: state.transactions.loading,
    transactions: state.transactions.transactionsData,
    page: state.transactions.page,
    size: state.transactions.size,
    total: state.transactions.total,
  }),
  {
    fetch: transactionActions.fetchRequest,
    clear: transactionActions.clear,
    setTransactionsList: transactionActions.setTransactionsList
  }
);

type TPropsFromRedux = ConnectedProps<typeof connector>;

export default connector(TransactionPage);
